import { useState } from 'react';
import type { Comment } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

function pinPosition(c: Comment) {
  if (c.nodeId && app.doc.has(c.nodeId)) {
    const b = app.doc.worldBounds(c.nodeId);
    return app.pageToScreen({ x: b.x + c.x, y: b.y + c.y });
  }
  return app.pageToScreen({ x: c.x, y: c.y });
}

function initial(name: string) {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/** DOM overlay with comment pins, thread popovers and the new-comment composer. */
export function CommentsLayer() {
  useApp(['comments', 'camera', 'doc', 'ui'], () => app.comments.length);
  const page = app.editor.pageId;
  const visible = app.comments.filter((c) => c.pageId === page && (app.ui.showResolved || !c.resolved));
  const draft = app.ui.commentDraft;
  const open = app.comments.find((c) => c.id === app.ui.openCommentId);
  const draftPos = draft ? (draft.nodeId && app.doc.has(draft.nodeId) ? app.pageToScreen({ x: app.doc.worldBounds(draft.nodeId).x + draft.x, y: app.doc.worldBounds(draft.nodeId).y + draft.y }) : app.pageToScreen(draft)) : null;
  return (
    <div className="comments-layer">
      {visible.map((c) => {
        const p = pinPosition(c);
        return (
          <button
            key={c.id}
            className={`comment-pin ${c.resolved ? 'resolved' : ''} ${open?.id === c.id ? 'open' : ''}`}
            style={{ left: p.x, top: p.y, background: c.color }}
            title={`${c.author}: ${c.text}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => app.setUI({ openCommentId: open?.id === c.id ? null : c.id, commentDraft: null })}
          >
            {c.authorKind === 'agent' ? Icons.sparkle : initial(c.author)}
            {c.replies.length > 0 && <span className="count">{c.replies.length + 1}</span>}
          </button>
        );
      })}
      {open && <Thread c={open} pos={pinPosition(open)} />}
      {draft && draftPos && <Composer pos={draftPos} />}
    </div>
  );
}

function Composer({ pos }: { pos: { x: number; y: number } }) {
  const [text, setText] = useState('');
  const submit = () => {
    const d = app.ui.commentDraft;
    if (!d || !text.trim()) return;
    app.sync?.comment({ action: 'add', pageId: app.editor.pageId, x: d.x, y: d.y, nodeId: d.nodeId, text: text.trim() });
    app.setUI({ commentDraft: null });
  };
  return (
    <div className="comment-card" style={{ left: pos.x + 18, top: pos.y - 8 }} onPointerDown={(e) => e.stopPropagation()}>
      <textarea
        autoFocus
        placeholder="Add a comment… (agents can read and reply)"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape') app.setUI({ commentDraft: null });
        }}
      />
      <div className="actions">
        <button className="btn ghost" onClick={() => app.setUI({ commentDraft: null })}>
          Cancel
        </button>
        <button className="btn primary" disabled={!text.trim()} onClick={submit}>
          Post
        </button>
      </div>
    </div>
  );
}

function Thread({ c, pos }: { c: Comment; pos: { x: number; y: number } }) {
  const [text, setText] = useState('');
  const reply = () => {
    if (!text.trim()) return;
    app.sync?.comment({ action: 'reply', commentId: c.id, text: text.trim() });
    setText('');
  };
  const msgs = [{ id: c.id, author: c.author, authorKind: c.authorKind, color: c.color, text: c.text, createdAt: c.createdAt }, ...c.replies];
  return (
    <div className="comment-card thread" style={{ left: pos.x + 18, top: pos.y - 8 }} onPointerDown={(e) => e.stopPropagation()}>
      <div className="thread-head">
        <span>{c.resolved ? 'Resolved' : 'Comment'}</span>
        <span style={{ display: 'flex', gap: 4 }}>
          <button className="btn ghost" onClick={() => app.sync?.comment({ action: 'resolve', commentId: c.id, resolved: !c.resolved })}>
            {c.resolved ? 'Reopen' : '✓ Resolve'}
          </button>
          <button className="icon-btn" title="Delete thread" onClick={() => (app.sync?.comment({ action: 'delete', commentId: c.id }), app.setUI({ openCommentId: null }))}>
            {Icons.minus}
          </button>
          <button className="icon-btn" onClick={() => app.setUI({ openCommentId: null })}>
            {Icons.close}
          </button>
        </span>
      </div>
      <div className="msgs">
        {msgs.map((m) => (
          <div key={m.id} className="msg">
            <span className={`who ${m.authorKind === 'agent' ? 'agent' : ''}`} style={{ background: m.color }}>
              {m.authorKind === 'agent' ? Icons.sparkle : initial(m.author)}
            </span>
            <div>
              <b>{m.author}</b> <span className="time">{new Date(m.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              <div className="text">{m.text}</div>
            </div>
          </div>
        ))}
      </div>
      <textarea
        placeholder="Reply…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            reply();
          }
        }}
      />
    </div>
  );
}
